import { useEffect, useMemo, useRef, useState } from "react";
import { getCoinIconCandidates } from "@/lib/coinIcon";

export function fmtPrice(n) {
  if (!Number.isFinite(n)) return "—";
  if (n >= 1000) return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (n >= 1) return n.toFixed(2);
  if (n >= 0.01) return n.toFixed(4);
  return n.toFixed(6);
}

export function fmtCompact(n) {
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 2 }).format(n);
}

export function PriceCell({ value, prefix = "$", className = "" }) {
  const prev = useRef(value);
  const [flash, setFlash] = useState("");

  useEffect(() => {
    if (value === prev.current) return undefined;
    const dir = value > prev.current ? "flash-up" : "flash-down";
    prev.current = value;
    setFlash("");
    const raf = requestAnimationFrame(() => setFlash(dir));
    const t = setTimeout(() => setFlash(""), 950);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t);
    };
  }, [value]);

  return (
    <span className={`num inline-block rounded px-1.5 py-0.5 ${flash} ${className}`} data-testid="mkt-price-cell">
      {prefix}
      {fmtPrice(value)}
    </span>
  );
}

export function ChangePill({ value }) {
  const v = Number.isFinite(value) ? value : 0;
  const up = v >= 0;
  return (
    <span
      className={`num inline-flex min-w-0 justify-center rounded-md px-1.5 py-0.5 text-xs font-medium sm:min-w-[68px] sm:px-2 ${
        up ? "bg-up/[0.12] text-up" : "bg-down/[0.12] text-down"
      }`}
    >
      {up ? "+" : ""}
      {v.toFixed(2)}%
    </span>
  );
}

const TINTS = {
  BTC: "#F7931A",
  ETH: "#627EEA",
  SOL: "#14F195",
  USDT: "#26A17B",
  USDC: "#2775CA",
  BNB: "#F3BA2F",
  XRP: "#23292F",
  ARB: "#12AAFF",
  TIA: "#7B2BF9",
  MATIC: "#8247E5",
  POL: "#8247E5",
  AVAX: "#E84142",
  DOGE: "#C2A633",
  ADA: "#0033AD",
  TRX: "#EB0029",
  DOT: "#E6007A",
  LTC: "#345D9D",
  LINK: "#2A5ADA",
};

export function tintOf(symbol = "") {
  const s = symbol.toUpperCase();
  if (TINTS[s]) return TINTS[s];
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) % 360;
  return `hsl(${h} 70% 55%)`;
}

export function AssetGlyph({ symbol, image, tint, size = 30 }) {
  const color = tint || tintOf(symbol);
  const candidates = useMemo(() => getCoinIconCandidates(symbol, image), [symbol, image]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    setIdx(0);
  }, [symbol, image]);

  if (idx < candidates.length) {
    return (
      <img
        src={candidates[idx]}
        alt=""
        width={size}
        height={size}
        className="shrink-0 rounded-full bg-surface-2 object-cover"
        style={{ width: size, height: size }}
        loading="lazy"
        decoding="async"
        referrerPolicy="no-referrer"
        onError={() => setIdx((i) => i + 1)}
      />
    );
  }

  return (
    <span
      className="inline-flex shrink-0 items-center justify-center rounded-full text-[11px] font-semibold"
      style={{
        width: size,
        height: size,
        backgroundColor: `color-mix(in srgb, ${color} 13%, transparent)`,
        color,
        border: `1px solid color-mix(in srgb, ${color} 27%, transparent)`,
      }}
    >
      {String(symbol || "?").slice(0, 3)}
    </span>
  );
}

export function Sparkline({ data, width = 96, height = 28, up = true, fill = false, className }) {
  if (!data || data.length < 2) return <div className="skeleton" style={{ width, height }} />;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const span = max - min || 1;
  const step = width / (data.length - 1);
  const pts = data.map((v, i) => `${(i * step).toFixed(2)},${(height - ((v - min) / span) * (height - 4) - 2).toFixed(2)}`);
  const stroke = up ? "oklch(var(--up))" : "oklch(var(--down))";
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className={className} aria-hidden>
      {fill && <polygon points={`0,${height} ${pts.join(" ")} ${width},${height}`} fill={stroke} opacity={0.12} />}
      <polyline points={pts.join(" ")} fill="none" stroke={stroke} strokeWidth={1.6} strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}
